import { useLazyQuery, useQuery } from "@apollo/client";
import Avatar from "components/core/Avatar";
import {
  ISSUER_SEARCH_RESULTS_ATOM,
  SELECTED_ISSUERS_ATOM,
} from "components/charts/modern/overlay/IssuersSelect/atoms";
import getHairlineColor from "components/charts/modern/overlay/getHairlineColor";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import IssuerPeersQuery from "lib/graphql/queries/IssuerPeersQuery";
import SearchIssuersQuery from "lib/graphql/queries/SearchIssuersQuery";
import type {
  IIssuerPeersQuery,
  IIssuerPeersQueryVariables,
} from "lib/graphql/queries/__generated__/IssuerPeersQuery.generated";
import type {
  ISearchIssuersQuery,
  ISearchIssuersQueryVariables,
} from "lib/graphql/queries/__generated__/SearchIssuersQuery.generated";
import { uniqBy } from "lodash";
import debounce from "lodash/fp/debounce";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useState } from "react";
import type { IIssuerListingCountry } from "types/graphqlTypes";
import { IIssuerSearchNarrowCriteria } from "types/graphqlTypes";

export type TIssuerPeer = IIssuerPeersQuery["issuerPeers"][number];

interface IssuerSearchInputProps {
  debounceMs?: number;
  placeholder?: string;
  noResultsText?: string;
  queryVariables?: Partial<Omit<ISearchIssuersQueryVariables, "query">>;
  disabled?: boolean;
}
function IssuerSearchInput({
  debounceMs = 200,
  placeholder,
  noResultsText = "Nada...",
  disabled = false,
  queryVariables,
}: IssuerSearchInputProps) {
  const setIssuerSearchResults = useSetAtom(ISSUER_SEARCH_RESULTS_ATOM);
  const [performSearch, { loading, data, called, variables }] = useLazyQuery<
    ISearchIssuersQuery,
    ISearchIssuersQueryVariables
  >(SearchIssuersQuery, {
    onCompleted(data) {
      if (data && data.searchIssuers && data.searchIssuers.length > 0) {
        setIssuerSearchResults(data.searchIssuers);
      } else {
        setIssuerSearchResults([]);
      }
    },
  });
  const handleSetQueryText = useCallback(
    debounce(debounceMs, (queryText) => {
      performSearch({
        variables: {
          issuerType: null,
          listingCountry: null,
          narrowContextIssuer: null,
          narrowCriteria: [],
          excludeIssuers: [],
          ...queryVariables,
          query: queryText,
        },
      });
    }),
    [],
  );

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        name="tickerSymbol"
        aria-label="Ticker"
        maxLength={6}
        onKeyDown={(event) => {
          // Only allow alphanumeric characters
          if (!/^[a-zA-Z0-9.]+$/.test(event.key)) {
            event.preventDefault();
          }
        }}
        onChange={(event) => handleSetQueryText(event.target.value)}
        spellCheck={false}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        className={classNames(
          "block w-full rounded-md border-0 px-3 py-3 pr-14 text-gray-900 shadow-sm",
          "bg-white ring-1 ring-inset ring-slate-300/60",
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-slate-100 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-300",
          "font-mono text-sm",
          "font-bold uppercase tracking-widest text-slate-700",
          "focus:placeholder:font-normal focus:placeholder:normal-case focus:placeholder:tracking-normal",
          "placeholder:font-normal placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400",
          "focus:placeholder:text-slate-500",
          "disabled:bg-gray-50 disabled:opacity-50",
        )}
      />

      <div className="absolute inset-y-0 right-0 flex space-x-2 py-2 pr-2">
        {loading ? (
          <span className="inline-flex items-center px-3">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" strokeWidth={3} />
          </span>
        ) : (
          <>
            <AnimatePresence>
              {called && variables && variables.query !== "" && (data?.searchIssuers || []).length === 0 && (
                <motion.span
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  className="inline-flex items-center rounded-md border border-rose-100 bg-rose-50/50 px-1.5 text-xs font-bold text-rose-500"
                >
                  {noResultsText}
                </motion.span>
              )}
            </AnimatePresence>

            <kbd className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-xs font-bold text-slate-500">
              Esc
            </kbd>
          </>
        )}
      </div>
    </div>
  );
}

interface IssuersSelectProps {
  children: ReactNode;
  issuerId: string;
  listingCountry: IIssuerListingCountry;
  title?: ReactNode;
  description?: ReactNode;
  inputSearchPlaceholder?: string;
  inputSearchNoResultsText?: string;
  issuerSelectionLimit?: number;
}
export default function IssuersSelect({
  title = "Pesquise empresas",
  description = (
    <>
      Utilize o ticker completo, por exemplo: <strong className="font-medium">WEGE3</strong>
    </>
  ),
  inputSearchPlaceholder = "Digite um ticker completo...",
  inputSearchNoResultsText = "Nada...",
  issuerSelectionLimit = 4,
  children,
  issuerId,
  listingCountry,
}: IssuersSelectProps) {
  const [selectedIssuers, setSelectedIssuers] = useAtom(SELECTED_ISSUERS_ATOM);
  const issuerSearchResults = useAtomValue(ISSUER_SEARCH_RESULTS_ATOM);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { data, loading } = useQuery<IIssuerPeersQuery, IIssuerPeersQueryVariables>(IssuerPeersQuery, {
    skip: !isDialogOpen,
    variables: {
      contextIssuer: issuerId,
      listingCountry,
      top: 3,
      narrowCriteria: [IIssuerSearchNarrowCriteria.SameType, IIssuerSearchNarrowCriteria.SameFinnancialsTemplate],
    },
  });

  // TODO: Add the ability to exclude selected issuers from the list
  const issuers = uniqBy(
    [...issuerSearchResults, ...selectedIssuers, ...(data?.issuerPeers || [])],
    (issuer) => issuer._id,
  );
  return (
    <Dialog open={isDialogOpen} onOpenChange={(isOpen) => setIsDialogOpen(isOpen)}>
      <DialogTrigger asChild={true}>{children}</DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 bg-white sm:rounded-3xl">
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-sm italic tracking-wide text-slate-500">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <IssuerSearchInput
            placeholder={inputSearchPlaceholder}
            noResultsText={inputSearchNoResultsText}
            queryVariables={{
              excludeIssuers: [issuerId, ...(data?.issuerPeers || []).map((node) => node._id)],
            }}
            disabled={selectedIssuers.length >= issuerSelectionLimit}
          />

          <div className="-mx-2 flex flex-col items-start">
            {loading && (
              <div className="w-full animate-pulse space-y-4 px-2">
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
                <div className="flex h-12 w-full rounded-lg bg-slate-100 outline-none ring-1 ring-slate-200 ring-offset-2 ring-offset-white" />
              </div>
            )}

            <AnimatePresence initial={false}>
              {!loading &&
                issuers.map((issuer) => {
                  const hairlineColor = getHairlineColor(issuer, true);
                  const isSelected = selectedIssuers.some((candidate) => {
                    return candidate._id === issuer._id;
                  });
                  return (
                    <motion.button
                      type="button"
                      key={issuer._id}
                      initial={{ opacity: 0, y: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 0 }}
                      disabled={!isSelected && selectedIssuers.length >= issuerSelectionLimit}
                      className={classNames(
                        "group my-1.5 flex w-full flex-1 items-center space-x-3 rounded-lg px-2 py-1",
                        "ring-1 ring-transparent ring-offset-2 ring-offset-transparent focus-visible:outline-none",
                        "focus-visible:bg-slate-50 focus-visible:ring-slate-200 focus-visible:ring-offset-white",
                        {
                          "bg-violet-50 ring-1 ring-violet-200 ring-offset-2 ring-offset-white": isSelected,
                          "focus-visible:bg-violet-50 focus-visible:ring-violet-200 focus-visible:ring-offset-white":
                            isSelected,
                        },
                      )}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedIssuers(selectedIssuers.filter((candidate) => candidate._id !== issuer._id));
                        } else if (selectedIssuers.length < issuerSelectionLimit) {
                          setSelectedIssuers([...selectedIssuers, issuer]);
                        }
                      }}
                    >
                      <Avatar
                        src={issuer.niceLogo?.url || null}
                        hashValue={issuer._id}
                        alt={issuer.name}
                        size={42}
                        squircle={true}
                      />

                      <div className="w-full py-0.5 text-left">
                        <strong className="text-sm font-bold tracking-tight text-slate-800">{issuer.name}</strong>
                        <span className="flex truncate text-xs font-medium italic tracking-wide text-slate-500">
                          {issuer.sector}
                        </span>
                      </div>

                      <div className="ml-auto w-full space-x-3 text-right">
                        <span
                          className={classNames(
                            "transition-none duration-100 ease-in-out",
                            "rounded-xl px-2 py-1 text-xs tracking-wider dark:bg-gray-700 dark:text-gray-300",
                            "bg-violet-50 font-mono font-semibold text-violet-600 group-hover:bg-violet-100",
                            "group-focus-visible:bg-slate-500 group-focus-visible:text-white",
                          )}
                        >
                          {issuer.tickerSymbol}
                        </span>

                        <span
                          aria-hidden={true}
                          className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700 opacity-40 group-hover:opacity-80"
                          style={{ backgroundColor: hairlineColor || undefined }}
                        />
                      </div>
                    </motion.button>
                  );
                })}
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
